import { useState } from 'react';
import { jsPDF } from 'jspdf';
import { toast } from 'sonner';
import { clean, safeName } from '../lib/exporters';
import { companyLine } from '../lib/company';
import { locale } from '../lib/units';
import DateSel from './DateSel';

// DIN EN 1610 leak tightness test per sewer section / manhole (air LA–LD or water W) -> stored as obj.dp, PDF certificate
export const DP_METHODS = ['LA', 'LB', 'LC', 'LD', 'W'];
const num = (v) => { const n = parseFloat(String(v ?? '').replace(',', '.')); return Number.isFinite(n) ? n : null; };
// pass rule: air -> pressure drop <= allowed Δp, water -> added water <= allowed litres
export const dpPass = (d) => {
  if (!d) return null;
  const a = num(d.meas), b = num(d.allow);
  return a == null || b == null ? null : a <= b;
};

export function dpPdf(obj, project, t) {
  const d = obj.dp || {}, doc = new jsPDF({ unit: 'mm', format: 'a4' }), M = 18, ok = dpPass(d);
  doc.setFillColor(8, 145, 178); doc.rect(0, 0, 210, 26, 'F'); doc.setTextColor(255);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15); doc.text(clean(t.dpCert.replace(' (PDF)', '')), M, 12);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9); doc.text(clean([companyLine(), project.title].filter(Boolean).join(' · ')), M, 20);
  doc.setTextColor(20); let y = 40;
  const row = (k, v) => { doc.setFont('helvetica', 'bold'); doc.text(clean(k), M, y); doc.setFont('helvetica', 'normal'); doc.text(clean(String(v ?? '–')), M + 85, y); y += 7; };
  doc.setFontSize(10.5);
  row(t.dpObj, obj.name); row(t.dpNorm, 'DIN EN 1610'); row(t.dpMethod, d.method ? t['dpM_' + d.method] : '–');
  row(t.dpDate, d.date ? new Date(`${d.date}T12:00`).toLocaleDateString(locale(), { day: '2-digit', month: '2-digit', year: 'numeric' }) : '–');
  row(t.dpP0, d.p0 ? `${d.p0} ${d.method === 'W' ? 'kPa' : 'mbar'}` : '–'); row(t.dpTime, d.time ? `${d.time} min` : '–');
  row(d.method === 'W' ? t.dpMeasW : t.dpMeasA, d.meas ?? '–'); row(d.method === 'W' ? t.dpAllowW : t.dpAllowA, d.allow ?? '–');
  row(t.dpTester, d.tester || '–'); if (d.note) row(t.dpNote, d.note);
  y += 6; doc.setFontSize(16); doc.setFont('helvetica', 'bold');
  if (ok === true) doc.setTextColor(22, 163, 74); else if (ok === false) doc.setTextColor(220, 38, 38);
  doc.text(clean(ok == null ? t.dpOpen : ok ? t.dpOk : t.dpFail), M, y); doc.setTextColor(20);
  y += 30; doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.line(M, y, M + 70, y); doc.line(M + 100, y, M + 170, y);
  doc.text(clean(t.dpSignTester), M, y + 5); doc.text(clean(t.dpSignClient), M + 100, y + 5);
  doc.save(`${safeName(obj.name || 'Pruefung')}_Dichtheitspruefung.pdf`);
}

export default function LeakTest({ t, obj, project, patch }) {
  const [open, setOpen] = useState(!!obj.dp);
  const d = obj.dp || {}, ok = dpPass(obj.dp);
  const set = (k) => (v) => patch({ dp: { ...d, [k]: v?.target ? v.target.value : v } });
  const inp = (k, mode = 'decimal') => <input data-testid={`dp-${k}`} inputMode={mode} value={d[k] ?? ''} onChange={set(k)} />;
  if (!open) return <button type="button" className="btn btn-outline btn-sm" data-testid="dp-open-btn" onClick={() => setOpen(true)}>🧪 {t.dtTitle}</button>;
  return (
    <div className="dp-box" data-testid="dp-box">
      <div className="si-head"><b>🧪 {t.dtTitle}</b>{ok != null && <span className={`dp-res ${ok ? 'ok' : 'bad'}`} data-testid="dp-result">{ok ? t.dpOk : t.dpFail}</span>}</div>
      <div className="hk-grid">
        <label>{t.dpMethod}<select data-testid="dp-method" value={d.method || ''} onChange={set('method')}><option value="">–</option>{DP_METHODS.map((m) => <option key={m} value={m}>{t['dpM_' + m]}</option>)}</select></label>
        <label>{t.dpDate}<DateSel past={25} testid="dp-date" value={d.date} onChange={set('date')} /></label>
        <label>{t.dpP0} ({d.method === 'W' ? 'kPa' : 'mbar'}){inp('p0')}</label>
        <label>{t.dpTime} (min){inp('time')}</label>
        <label>{d.method === 'W' ? t.dpMeasW : t.dpMeasA}{inp('meas')}</label>
        <label>{d.method === 'W' ? t.dpAllowW : t.dpAllowA}{inp('allow')}</label>
        <label>{t.dpTester}{inp('tester', 'text')}</label>
        <label>{t.dpNote}{inp('note', 'text')}</label>
      </div>
      <button type="button" className="btn btn-dark btn-sm" data-testid="dp-pdf-btn" onClick={() => { dpPdf(obj, project, t); toast.success(t.dpCert); }}>📄 {t.dpCert}</button>
    </div>
  );
}
